import {useEffect, useMemo, useState} from 'react';
import {SVG} from '../../../../../../constants/assetLists';
import {getCorrectPath} from '../../../../../../utils';

type Props = {
  file: File | string;
  onRemove?: () => void;
  className?: string;
};

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'aac', 'ogg'];
const PDF_EXTENSIONS = ['pdf'];
const WORD_EXTENSIONS = ['doc', 'docx'];

function getExtension(nameOrUrl: string): string {
  return (nameOrUrl.split('.').pop() || '').split('?')[0].toLowerCase();
}

function getFileName(file: File | string): string {
  if (typeof file !== 'string') return file.name;
  return file.split('/').pop()?.split('?')[0] || '';
}

function FilePreview({file, onRemove, className = ''}: Props) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const fileName = getFileName(file);
  const extension = getExtension(fileName);
  const displayName = fileName;

  const fileType = typeof file !== 'string' ? file.type : '';

  const isImage =
    fileType.startsWith('image') || IMAGE_EXTENSIONS.includes(extension);
  const isVideo =
    fileType.startsWith('video') || VIDEO_EXTENSIONS.includes(extension);
  const isPdf =
    fileType === 'application/pdf' || PDF_EXTENSIONS.includes(extension);
  const isWord =
    fileType === 'application/msword' ||
    fileType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    WORD_EXTENSIONS.includes(extension);
  const isAudio =
    fileType.startsWith('audio') || AUDIO_EXTENSIONS.includes(extension);

  const icon = useMemo(() => {
    if (isPdf) return SVG.pdfIcon;
    if (isWord) return SVG.wordIcon;
    if (isAudio) return SVG.micIcon;
    return SVG.fileIcon;
  }, [isPdf, isWord, isAudio]);

  useEffect(() => {
    if (typeof file === 'string') {
      setPreviewUrl(file);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const openUrl = useMemo(() => {
    if (typeof file === 'string') return getCorrectPath(file, fileType);
    return previewUrl;
  }, [file, fileType, previewUrl]);

  const handleOpen = () => {
    if (!openUrl || isImage || isVideo) return;
    window.open(openUrl, '_blank');
  };

  if (!previewUrl) return null;

  if (isImage) {
    return (
      <div
        className={`relative w-[280px] sm:!w-[333px]  h-[301px] border border-gray70 rounded-lg flex items-center justify-center p-4 ${className}`}>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-4 left-4 z-10">
            <img
              src={SVG.closeDarkIcon}
              alt="remove"
              className="w-[14px] h-[14px] object-contain"
            />
          </button>
        )}
        <img
          src={previewUrl}
          alt="preview"
          className="max-w-[240px] max-h-[240px] object-contain"
        />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div
        className={`relative w-[280px] sm:!w-[333px]  h-[301px] border border-gray70 rounded-lg flex items-center justify-center p-4 ${className}`}>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-4 left-4 z-10">
            <img
              src={SVG.closeDarkIcon}
              alt="remove"
              className="w-[14px] h-[14px] object-contain"
            />
          </button>
        )}
        <video autoPlay controls muted width={240} height={240}>
          <source src={previewUrl} type={fileType} />
        </video>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') handleOpen();
      }}
      className={`w-[280px] sm:!w-[333px] px-3 py-2 border border-[#8080808C] rounded-lg bg-white flex justify-between items-center gap-2 cursor-pointer ${className}`}>
      <div className="flex items-center gap-2 overflow-hidden">
        <div className="w-[24px] h-[24px] flex-shrink-0">
          <img src={icon} alt="icon" className="w-full h-full object-contain" />
        </div>
        <span className="flex-1 text-xl font-medium truncate">
          {displayName}
        </span>
      </div>

      {onRemove && (
        <button
          type="button"
          className="w-[14px] h-[14px] flex-shrink-0 hover:opacity-40 transition-opacity duration-200"
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}>
          <img
            src={SVG.closeDarkIcon}
            alt="remove"
            className="w-full h-full object-contain"
          />
        </button>
      )}
    </div>
  );
}

export default FilePreview;
